<payee-popup width="1080px">
  <ng-template #payeePopupTitleTemplate>
    <h1 class="title">
      {{ "WORKFLOW_DIALOGS.DEBTOR_STATEMENT.TITLE" | translate }}
    </h1>
  </ng-template>

  <ng-template #payeePopupDescriptionTemplate>
    <div class="description">
      {{ "WORKFLOW_DIALOGS.DEBTOR_STATEMENT.DESCRIPTION" | translate }}
    </div>
  </ng-template>

  <form [formGroup]="form">
    <payee-form-field
      class="deadline-field"
      width="max"
    >
      <span payee-label>
        {{ "WORKFLOW_DIALOGS.DEBTOR_STATEMENT.DEADLINE" | translate }}
      </span>
      <payee-datepicker
        [formControl]="form.controls.hard_deadline_at"
        [min]="minDeadline"
      ></payee-datepicker>
      @if (form.controls.hard_deadline_at.touched && form.controls.hard_deadline_at.hasError("required")) {
        <ng-container payee-error>
          <span>{{ "ERROR_MESSAGES.FIELD_REQUIRED" | translate }}</span>
        </ng-container>
      }
    </payee-form-field>

    <div class="claims">
      @for (claimForm of form.controls.principal_claims.controls; track claimForm.value.id; let index = $index) {
        @let claim = principalRow(claimForm.value.id);

        @if (claim) {
          <div class="claim-block">
            <div class="claim-details flex vcenter">
              <div class="claim-number">
                {{ index + 1 }}.
                {{ "WORKFLOW_DIALOGS.DEBTOR_STATEMENT.CLAIM" | translate }}
              </div>
              <div class="claim-due-date">
                @if (claim.due_date) {
                  {{ "WORKFLOW_DIALOGS.DEBTOR_STATEMENT.DUE_DATE" | translate }}
                  {{ claim.due_date | date: "yyyy.MM.dd." : "UTC" }}
                }
              </div>
              <div class="claim-value">
                <span class="claim-value-label">
                  {{ "WORKFLOW_DIALOGS.DEBTOR_STATEMENT.TOTAL" | translate }}
                </span>
                <span>
                  {{ claim.total | currency: claim.currency : "symbol-narrow" : "1.0" : "hu" }}
                </span>
              </div>
              <button
                type="button"
                payee-icon-button
                color="white"
                class="claim-remove-button"
                [attr.aria-label]="'WORKFLOW_DIALOGS.DEBTOR_STATEMENT.REMOVE' | translate"
                (click)="removePrincipal(index)"
              >
                <payee-icon name="trash-red"></payee-icon>
              </button>
            </div>

            <ng-container
              [ngTemplateOutlet]="amountFields"
              [ngTemplateOutletContext]="{ claimForm, claim }"
            ></ng-container>

            @for (optionalForm of claimForm.controls.optional_claims.controls; track optionalForm.value.id; let optionalIndex = $index) {
              @let optionalRow = optionalClaim(claim.id, optionalForm.value.id);

              @if (optionalRow) {
                <div class="optional-claim-details flex vcenter">
                  <div class="optional-claim-name">
                    {{ optionalRow.label }}
                  </div>
                  <div class="optional-claim-value">
                    <span class="claim-value-label">
                      {{ "WORKFLOW_DIALOGS.DEBTOR_STATEMENT.TOTAL" | translate }}
                    </span>
                    <span>
                      {{ optionalRow.total | currency: optionalRow.currency : "symbol-narrow" : "1.0" : "hu" }}
                    </span>
                  </div>
                  <button
                    type="button"
                    payee-icon-button
                    color="white"
                    class="claim-remove-button"
                    [attr.aria-label]="'WORKFLOW_DIALOGS.DEBTOR_STATEMENT.REMOVE' | translate"
                    (click)="removeOptional(claimForm, optionalIndex)"
                  >
                    <payee-icon name="trash-red"></payee-icon>
                  </button>
                </div>
                <div class="optional-paid">
                  <ng-container
                    [ngTemplateOutlet]="amountFields"
                    [ngTemplateOutletContext]="{ claimForm: optionalForm, claim: optionalRow }"
                  ></ng-container>
                </div>
              }
            }

            @if (availableOptional(claim.id).length) {
              <div class="add-optional">
                @for (optional of availableOptional(claim.id); track optional.id) {
                  <button
                    type="button"
                    class="add-optional-button"
                    (click)="addOptional(claimForm, optional)"
                  >
                    <payee-icon
                      name="plus-blue"
                      width="16px"
                      height="16px"
                    ></payee-icon>
                    <span>
                      {{ optional.label }}
                      · {{ optional.total | currency: optional.currency : "symbol-narrow" : "1.0" : "hu" }}
                    </span>
                  </button>
                }
              </div>
            }
          </div>
        }
      }

      @if (availablePrincipals().length) {
        <div class="add-principal-list">
          @for (principal of availablePrincipals(); track principal.id) {
            <div class="add-principal">
              <button
                type="button"
                class="add-optional-button"
                (click)="addPrincipal(principal)"
              >
                <payee-icon
                  name="plus-blue"
                  width="16px"
                  height="16px"
                ></payee-icon>
                <span>
                  {{ principal.label }}
                  @if (principal.due_date) {
                    · {{ principal.due_date | date: "yyyy.MM.dd." : "UTC" }}
                  }
                  · {{ principal.total | currency: principal.currency : "symbol-narrow" : "1.0" : "hu" }}
                </span>
              </button>
              @if (ancillaries(principal).length) {
                <div class="add-optional">
                  @for (optional of ancillaries(principal); track optional.id) {
                    <button
                      type="button"
                      class="add-optional-button"
                      (click)="addAncillary(principal, optional)"
                    >
                      <payee-icon
                        name="plus-blue"
                        width="16px"
                        height="16px"
                      ></payee-icon>
                      <span>
                        {{ optional.label }}
                        · {{ optional.total | currency: optional.currency : "symbol-narrow" : "1.0" : "hu" }}
                      </span>
                    </button>
                  }
                </div>
              }
            </div>
          }
        </div>
      }

      @for (claimForm of form.controls.fee_claims.controls; track claimForm.value.id; let feeIndex = $index) {
        @let claim = feeRow(claimForm.value.id);

        @if (claim) {
          <div class="claim-block">
            <div class="claim-details fee-claim-details flex vcenter">
              <div class="claim-name">{{ claim.label }}</div>
              <div class="claim-value">
                <span class="claim-value-label">
                  {{ "WORKFLOW_DIALOGS.DEBTOR_STATEMENT.TOTAL" | translate }}
                </span>
                <span>
                  {{ claim.total | currency: claim.currency : "symbol-narrow" : "1.0" : "hu" }}
                </span>
              </div>
              <button
                type="button"
                payee-icon-button
                color="white"
                class="claim-remove-button"
                [attr.aria-label]="'WORKFLOW_DIALOGS.DEBTOR_STATEMENT.REMOVE' | translate"
                (click)="removeFee(feeIndex)"
              >
                <payee-icon name="trash-red"></payee-icon>
              </button>
            </div>
            <ng-container
              [ngTemplateOutlet]="amountFields"
              [ngTemplateOutletContext]="{ claimForm, claim }"
            ></ng-container>
          </div>
        }
      }

      @if (availableFees().length) {
        <div class="add-optional">
          @for (fee of availableFees(); track fee.id) {
            <button
              type="button"
              class="add-optional-button"
              (click)="addFee(fee)"
            >
              <payee-icon
                name="plus-blue"
                width="16px"
                height="16px"
              ></payee-icon>
              <span>
                {{ fee.label }}
                · {{ fee.total | currency: fee.currency : "symbol-narrow" : "1.0" : "hu" }}
              </span>
            </button>
          }
        </div>
      }

      @if (claimsMissing()) {
        <div class="sum-error">
          {{ "WORKFLOW_DIALOGS.DEBTOR_STATEMENT.CLAIM_REQUIRED" | translate }}
        </div>
      }
    </div>

    <payee-workflow-provided-media [transitionWithDialog]="data"></payee-workflow-provided-media>

    <div class="subtitle">
      {{ "WORKFLOW_DIALOGS.DEBTOR_STATEMENT.FILES" | translate }}
    </div>

    <payee-form-field width="max">
      <payee-dropzone
        width="max"
        [heading]="'WORKFLOW_DIALOGS.DEBTOR_STATEMENT.FILES' | translate"
        (dropped)="onDrop($event)"
      ></payee-dropzone>
      @if (form.controls.files.invalid && form.controls.files.touched) {
        <span payee-error>
          {{ "WORKFLOW_DIALOGS.DEBTOR_STATEMENT.FILE_REQUIRED" | translate }}
        </span>
      }
    </payee-form-field>

    <div class="files-list">
      @for (file of form.controls.files.value; track $index) {
        <div class="file-item flex vcenter">
          <div class="file-name">{{ file.name }}</div>
          <button
            type="button"
            payee-icon-button
            color="white"
            class="file-delete-button"
            (click)="removeFile($index)"
          >
            <payee-icon name="trash-red"></payee-icon>
          </button>
        </div>
      }
    </div>

    <ng-template
      #amountFields
      let-claimForm="claimForm"
      let-claim="claim"
    >
      <div class="amount-fields">
        <payee-form-field width="max">
          <span payee-label>
            {{ "WORKFLOW_DIALOGS.DEBTOR_STATEMENT.ACKNOWLEDGED" | translate }}
          </span>
          <span payee-suffix>{{ claim.currency }}</span>
          <input
            payee-input
            inputmode="numeric"
            [formControl]="claimForm.controls.acknowledged"
          />
        </payee-form-field>
        <payee-form-field width="max">
          <span payee-label>
            {{ "WORKFLOW_DIALOGS.DEBTOR_STATEMENT.DISPUTED" | translate }}
          </span>
          <span payee-suffix>{{ claim.currency }}</span>
          <input
            payee-input
            inputmode="numeric"
            [formControl]="claimForm.controls.disputed"
          />
        </payee-form-field>
        <payee-form-field width="max">
          <span payee-label>
            {{ "WORKFLOW_DIALOGS.DEBTOR_STATEMENT.PAID_BEFORE" | translate }}
          </span>
          <span payee-suffix>{{ claim.currency }}</span>
          <input
            payee-input
            inputmode="numeric"
            [formControl]="claimForm.controls.paid_before"
          />
        </payee-form-field>
        <payee-form-field width="max">
          <span payee-label>
            {{ "WORKFLOW_DIALOGS.DEBTOR_STATEMENT.PAID_AFTER" | translate }}
          </span>
          <span payee-suffix>{{ claim.currency }}</span>
          <input
            payee-input
            inputmode="numeric"
            [formControl]="claimForm.controls.paid_after"
          />
        </payee-form-field>
      </div>
      @if (claimForm.hasError("sum") && (claimForm.dirty || claimForm.touched)) {
        <div class="sum-error">
          {{
            "WORKFLOW_DIALOGS.DEBTOR_STATEMENT.SUM_MISMATCH"
              | translate
                : {
                    total: (claim.total | number: "1.0" : "hu"),
                    currency: claim.currency,
                  }
          }}
        </div>
      }
    </ng-template>
  </form>

  <ng-template #payeePopupSubmitTemplate>
    <button
      class="payee-button"
      payee-button
      color="blue"
      width="max"
      height="normal"
      [disabled]="loading()"
      [loading]="loading()"
      (click)="submit()"
    >
      {{ "WORKFLOW_DIALOGS.DEBTOR_STATEMENT.SUBMIT" | translate }}
    </button>
  </ng-template>

  <ng-template #payeePopupCancelTemplate>
    <button
      class="payee-button"
      payee-button
      color="white"
      width="max"
      height="normal"
      [isBorderButton]="true"
      [mat-dialog-close]="false"
    >
      {{ "WORKFLOW_DIALOGS.DEBTOR_STATEMENT.CANCEL" | translate }}
    </button>
  </ng-template>
</payee-popup>
